import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';

const BackArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left-short" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5A.5.5 0 0 1 12 8" />
    </svg>
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getStatusClass = (status) => {
    switch (status) {
        case 'Aprobada':
        case 'approved':
            return 'bg-success';
        case 'Pendiente':
        case 'pending':
            return 'bg-warning text-dark';
        case 'Rechazada':
        case 'rejected':
            return 'bg-danger';
        default:
            return 'bg-light text-dark';
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'Aprobada':
        case 'approved':
            return 'Aprobada';
        case 'Pendiente':
        case 'pending':
            return 'Pendiente';
        case 'Rechazada':
        case 'rejected':
            return 'Rechazada';
        default:
            return 'Pendiente';
    }
};

const parseRequestId = (requestId) => {
    const initialMatch = requestId.match(/^user-(\d+)-initial$/);
    if (initialMatch) {
        return { type: 'initial', userId: initialMatch[1] };
    }
    const reqMatch = requestId.match(/^REQ-(\d+)-(\d+)$/);
    if (reqMatch) {
        return { type: 'trip', userId: reqMatch[1], tripId: reqMatch[2] };
    }
    return { type: 'trip', userId: null, tripId: requestId };
};

const extractTravelDateRange = (formObj) => {
    if (!formObj || typeof formObj !== 'object') return 'N/A';
    const key = Object.keys(formObj).find(k => k.startsWith('step1_4'));
    if (key && formObj[key]) {
        try {
            const val = typeof formObj[key] === 'string' ? JSON.parse(formObj[key]) : formObj[key];
            if (val && val.startDate && val.endDate) {
                return `${val.startDate} a ${val.endDate}`;
            }
        } catch (e) {
        }
    }
    return 'N/A';
};

const RequestViewScreen = () => {
    const { id: requestId } = useParams();
    const { token } = useAuth();
    const [requestDetails, setRequestDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quizData, setQuizData] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        const fetchRequestDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const parsed = parseRequestId(requestId);
                let applicantName = 'Nombre no disponible';
                let creationDate = 'N/A';
                let travelDateRange = 'N/A';
                let scheduleDate = 'N/A';
                let status = 'Pendiente';
                let rejectionReasonData = null;
                let answerData = {};
                let userId = null;
                let tripId = null;

                if (parsed.type === 'initial') {
                    userId = parsed.userId;
                    const userResponse = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!userResponse.ok) throw new Error('No se pudo obtener el usuario.');
                    const userData = await userResponse.json();

                    applicantName = userData.name || userData.username || userData.email || 'N/A';
                    creationDate = userData.created_at ? new Date(userData.created_at).toLocaleDateString() : 'N/A';

                    if (userData.initial_form_json) {
                        if (typeof userData.initial_form_json === 'string') {
                            try {
                                answerData = JSON.parse(userData.initial_form_json);
                            } catch (e) {
                                answerData = {};
                            }
                        } else if (typeof userData.initial_form_json === 'object') {
                            answerData = userData.initial_form_json;
                        }
                    }

                    travelDateRange = extractTravelDateRange(answerData);
                } else {
                    tripId = parsed.tripId;
                    const tripResponse = await fetch(`${API_BASE_URL}/api/user-trips/${tripId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!tripResponse.ok) throw new Error('No se pudo obtener el viaje.');
                    const tripData = await tripResponse.json();

                    const uid = parsed.userId || tripData.user_id;
                    userId = uid;
                    if (uid) {
                        const userResponse = await fetch(`${API_BASE_URL}/api/users/${uid}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (userResponse.ok) {
                            const userData = await userResponse.json();
                            applicantName = userData.name || userData.username || userData.email || 'N/A';
                        }
                    }

                    creationDate = tripData.created_at ? new Date(tripData.created_at).toLocaleDateString() : 'N/A';
                    scheduleDate = tripData.schedule_date ? new Date(tripData.schedule_date).toLocaleDateString() : 'N/A';
                    status = getStatusLabel(tripData.status);
                    rejectionReasonData = tripData.rejection_reason || null;

                    if (tripData.answer) {
                        if (typeof tripData.answer === 'string') {
                            try {
                                answerData = JSON.parse(tripData.answer);
                            } catch (e) {
                                answerData = {};
                            }
                        } else if (typeof tripData.answer === 'object') {
                            answerData = tripData.answer;
                        }
                    }

                    travelDateRange = extractTravelDateRange(answerData);
                }

                setRequestDetails({
                    id: requestId,
                    applicantName,
                    creationDate,
                    scheduleDate,
                    travelDateRange,
                    status,
                    statusClass: getStatusClass(status),
                    rejection_reason: rejectionReasonData,
                    answer: answerData,
                    requestType: parsed.type,
                    userId,
                    tripId,
                });

                const quizName = parsed.type === 'initial' ? 'Outer Questionnaire' : 'Inner Questionnaire';
                const quizzesResponse = await fetch(`${API_BASE_URL}/api/quizzes`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!quizzesResponse.ok) throw new Error('No se pudo obtener la lista de cuestionarios.');
                const quizzesData = await quizzesResponse.json();
                const targetQuiz = quizzesData.find(quiz => quiz.name === quizName);
                if (targetQuiz) {
                    const quizDetailResponse = await fetch(`${API_BASE_URL}/api/quizzes/${targetQuiz.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!quizDetailResponse.ok) throw new Error('No se pudo obtener el detalle del cuestionario.');
                    const fullQuizData = await quizDetailResponse.json();
                    setQuizData(fullQuizData);
                } else {
                    throw new Error(`No se encontró el cuestionario "${quizName}".`);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (token && requestId) {
            fetchRequestDetails();
        }
    }, [requestId, token]);

    const questionMap = useMemo(() => {
        if (!quizData?.questions) return new Map();
        return new Map(quizData.questions.map(q => [q.id, q]));
    }, [quizData]);

    const renderAnswerValue = (answerValue, questionSchema) => {
        if (answerValue === null || answerValue === undefined || answerValue === '') {
            return <span className="text-muted">No respondida</span>;
        }

        const allOptions = [...(questionSchema.options || [])];
        if (questionSchema.sections && Array.isArray(questionSchema.sections)) {
            questionSchema.sections.forEach(section => {
                if (section.options && Array.isArray(section.options)) {
                    allOptions.push(...section.options);
                }
            });
        }

        const getLabel = (val) => {
            const option = allOptions.find(opt => opt.value === val);
            if (option) {
                return option.text || option.label || option.mainText || option.id || val;
            }
            return val;
        };

        if ((questionSchema.type === 'multiSectionCheckboxGroup' || questionSchema.type === 'multiSectionSelectableOption') && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
            const selections = answerValue.selections || [];
            if (selections.length === 0) return <span className="text-muted">No respondida</span>;
            return selections.map(val => getLabel(val)).join(', ');
        }

        if (questionSchema.type.includes('WithOther') && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
            const selectionsKey = Object.keys(answerValue).find(k => Array.isArray(answerValue[k])) || 'selections';
            const otherDetailKey = Object.keys(answerValue).find(k => typeof answerValue[k] === 'string' && k !== selectionsKey) || 'otherDetail';

            const selections = answerValue[selectionsKey] || [];
            const otherDetail = answerValue[otherDetailKey] || '';
            const otherConfig = questionSchema.otherInputConfig;

            if (selections.length === 0) return <span className="text-muted">No respondida</span>;

            const labels = selections
                .map(val => {
                    if (otherConfig && val === otherConfig.optionValue) {
                        return otherDetail ? `${otherConfig.optionLabel}: ${otherDetail}` : otherConfig.optionLabel;
                    }
                    return getLabel(val);
                })
                .filter(Boolean)
                .join(', ');

            return labels || <span className="text-muted">No respondida</span>;
        }

        if (Array.isArray(answerValue)) {
            if (answerValue.length === 0) return <span className="text-muted">No respondida</span>;
            return answerValue.map(val => getLabel(val)).join(', ');
        }

        if (questionSchema.type === 'numberInputWithToggle' && typeof answerValue === 'object' && answerValue !== null) {
            const initialVal = questionSchema.initialValue || { amount: '', noFixedBudget: false };
            const amountKey = Object.keys(initialVal).find(k => typeof initialVal[k] === 'string' || typeof initialVal[k] === 'number') || 'amount';
            const toggleKey = Object.keys(initialVal).find(k => typeof initialVal[k] === 'boolean') || 'noFixedBudget';
            if (answerValue[toggleKey]) {
                return <span>No aplica</span>;
            }
            return <span>{answerValue[amountKey]}</span>;
        }

        if (typeof answerValue === 'object' && answerValue !== null) {
            if ('amount' in answerValue && 'noApply' in answerValue) {
                if (answerValue.noApply) {
                    return <span>No aplica</span>;
                }
                return <span>{answerValue.amount}</span>;
            }
            if ('startDate' in answerValue && 'endDate' in answerValue) {
                return <span>{answerValue.startDate} a {answerValue.endDate}</span>;
            }
            return <pre>{JSON.stringify(answerValue, null, 2)}</pre>;
        }

        const option = allOptions.find(opt => opt.value === String(answerValue));
        return option ? (option.text || option.label || option.mainText || option.id || String(answerValue)) : String(answerValue);
    };

    const handleApprove = async () => {
        if (requestDetails.requestType !== 'trip' && requestDetails.requestType !== 'initial') return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/user-trips/${requestDetails.tripId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: 'Aprobada' }),
            });
            if (!response.ok) throw new Error('No se pudo aprobar la solicitud.');
            setRequestDetails(prev => ({
                ...prev,
                status: 'Aprobada',
                statusClass: getStatusClass('Aprobada')
            }));
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReject = () => {
        setShowRejectModal(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectionReason.trim() || requestDetails.requestType !== 'trip' && requestDetails.requestType !== 'initial') return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/user-trips/${requestDetails.tripId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    status: 'Rechazada',
                    rejection_reason: rejectionReason
                }),
            });
            if (!response.ok) throw new Error('No se pudo rechazar la solicitud.');
            setRequestDetails(prev => ({
                ...prev,
                status: 'Rechazada',
                statusClass: getStatusClass('Rechazada')
            }));
            setShowRejectModal(false);
            setRejectionReason('');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <FormSpinnerOverlay isLoading={true} />;
    }

    if (error) {
        return <AlertMessage type="danger" message={`Error al cargar la solicitud: ${error}`} />;
    }

    if (!requestDetails) {
        return <AlertMessage type="info" message="No se encontraron detalles para esta solicitud." />;
    }

    const canApprove = requestDetails.status !== 'Aprobada' && requestDetails.status !== 'Rechazada' && (requestDetails.requestType === 'trip' || requestDetails.requestType === 'initial');

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-md-0 mb-4">Detalle de Solicitud</h1>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Link to="/requests" className="btn btn-outline-secondary me-2">
                        <BackArrowIcon />
                        Volver a Solicitudes
                    </Link>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body px-md-4 px-3 pb-5">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">ID Solicitud:</h5>
                                    <p>{requestDetails.id}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Nombre Solicitante:</h5>
                                    <p>{requestDetails.applicantName}</p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Fecha de Solicitud:</h5>
                                    <p>{requestDetails.creationDate}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Fecha de Viaje (Rango):</h5>
                                    <p>{requestDetails.travelDateRange}</p>
                                </div>
                            </div>
                            <div className="row">
                                {requestDetails.scheduleDate !== 'N/A' && (
                                    <div className="col-md-6 mb-3">
                                        <h5 className="text-muted">Fecha de Agendamiento:</h5>
                                        <p>{requestDetails.scheduleDate}</p>
                                    </div>
                                )}
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Estado Actual:</h5>
                                    <p><span className={`badge rounded-pill ${requestDetails.statusClass}`}>{requestDetails.status}</span></p>
                                </div>
                            </div>

                            {requestDetails.status === 'Rechazada' && requestDetails.rejection_reason && (
                                <>
                                    <hr className="my-4" />
                                    <h5 className="mb-4 text-danger">Motivo del Rechazo:</h5>
                                    <div className="alert alert-danger" role="alert">
                                        {requestDetails.rejection_reason}
                                    </div>
                                </>
                            )}

                            <hr className="my-4" />

                            <h5 className="mb-4">Respuestas del Solicitante</h5>
                            <div className="mb-3" id="supplierAnswersContainer">
                                {requestDetails.answer && Object.keys(requestDetails.answer).length > 0 && quizData ? (
                                    Object.entries(requestDetails.answer).map(([questionId, answerValue]) => {
                                        const questionSchema = questionMap.get(questionId);
                                        if (!questionSchema || questionSchema.type === 'hiddenInput') return null;

                                        return (
                                            <div key={questionId} className="mb-4">
                                                <div className="ps-md-2 mb-4">
                                                    <h4 className="supplier-question h6 fw-bold">{questionSchema.title}</h4>
                                                    <div className="supplier-answer">{renderAnswerValue(answerValue, questionSchema)}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <AlertMessage type="info" message="No se han encontrado respuestas para esta solicitud." />
                                )}
                            </div>

                            {canApprove && (
                                <div className="d-flex justify-content-end mt-4">
                                    <button type="button" className="btn btn-success d-flex align-items-center me-2" onClick={handleApprove}>
                                        Aprobar Solicitud
                                    </button>
                                    <button type="button" className="btn btn-danger d-flex align-items-center" onClick={handleReject}>
                                        Rechazar Solicitud
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showRejectModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="rechazarSolicitudModalLabel" aria-hidden="true">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="rechazarSolicitudModalLabel">Motivo del Rechazo</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="mb-3">
                                        <label htmlFor="motivoRechazoTexto" className="form-label">Por favor, especifique el motivo del rechazo:</label>
                                        <textarea
                                            className="form-control"
                                            id="motivoRechazoTexto"
                                            rows="4"
                                            placeholder="Ingrese aquí el motivo..."
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            required
                                        ></textarea>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={handleConfirmReject} disabled={!rejectionReason.trim()}>Confirmar Rechazo</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showRejectModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default RequestViewScreen;
